import moment from 'jalali-moment';
import { create } from 'zustand';
import { combine } from 'zustand/middleware';
import { STORAGE_CART_KEY } from '@/configs';
import storage from '../helpers/storage';

export interface CartProgram {
  id: number;
  selectedCount: number;
  food: any;
}

export interface CartSlice {
  expireAt: number;
  selectedSelfId: number | null;
  checkoutSelfDefId: number | null;
  weekStartDateTime: number | null;
  programs: CartProgram[];
  addToCart: (
    args: {
      id: number;
      food: UserWeekReserveEntity;
      checkoutSelfDefId?: number;
      weekStartDateTime?: number;
    },
    countToAdd?: number,
  ) => void;
  removeFromCart: (id: number, countToRemove?: number) => void;
  emptyBasket: () => void;
  remainingTime: () => number;
  remainingTimeString: () => string;
  findItemById: (id: number) => CartProgram;
  getTotalItemsCount: () => number;
  getMealCartCount: (programId: number) => number;
}

type CartPayload = Partial<CartSlice>

const CART_EXPIRE_TIME = 60 * 15;
// const CART_EXPIRE_TIME = 5;

const writeCartToStorage = (payload: any) => storage.writeJSON(STORAGE_CART_KEY, payload);

const readCartFromStorage = () => {
  const data = storage.readJSON(STORAGE_CART_KEY, {
    selectedSelfId: null,
    checkoutSelfDefId: null,
    weekStartDateTime: null,
    programs: [],
    expireAt: -1,
  }) as CartSlice & { expireAt: number };

  if (!data.programs?.length) {
    data.weekStartDateTime = null;
  }
  return data;
};

const initialState = readCartFromStorage() as CartSlice;

const mutations = (
  setState: (...params: Partial<CartSlice>[]) => any,
  getState: () => CartSlice
) => {
  return {
    addToCart: (
      { id, food, checkoutSelfDefId: selfDefId, weekStartDateTime: weekStart },
      countToAdd: number = 1,
    ) => {
      const programs = getState().programs;
      const programIndex = programs.findIndex(x => x.id === id);
      const expireAt = moment().unix() + CART_EXPIRE_TIME;
      const selectedSelfId = food.selfId ?? getState().selectedSelfId ?? null;
      const checkoutSelfDefId =
        selfDefId ?? getState().checkoutSelfDefId ?? selectedSelfId ?? null;
      const weekStartDateTime =
        programs.length === 0
          ? (weekStart ?? null)
          : (getState().weekStartDateTime ?? weekStart ?? null);

      let payload: CartPayload;
      if (programIndex < 0) {
        payload = {
          programs: [...programs, { id, food, selectedCount: countToAdd }],
          expireAt,
          selectedSelfId,
          checkoutSelfDefId,
          weekStartDateTime,
        };
      }
      else {
        programs[programIndex].selectedCount += countToAdd;
        if (programIndex >= 0 && programs[programIndex].food) {
          programs[programIndex].food = {
            ...programs[programIndex].food,
            ...food,
          };
        }
        payload = {
          programs: [...programs],
          expireAt,
          selectedSelfId,
          checkoutSelfDefId,
          weekStartDateTime,
        };
      }
      setState(payload);
      writeCartToStorage({ ...getState(), ...payload });
    },

    removeFromCart: (id: number, countToRemove = 1) => {

      const programs = getState().programs;
      const programIndex = programs.findIndex(x => x.id === id);

      if (programIndex < 0) return;
      programs[programIndex].selectedCount -= countToRemove;
      if (programs[programIndex].selectedCount < 0) {
        programs[programIndex].selectedCount = 0;
      }
      const isEmpy = programs[programIndex].selectedCount === 0;
      const nextPrograms = isEmpy ? programs.filter(p => p.id != id) : [...programs];
      const payload: CartPayload = {
        programs: nextPrograms,
        ...(nextPrograms.length === 0 ? { weekStartDateTime: null } : {}),
      };
      setState(payload);
      writeCartToStorage({ ...getState(), ...payload });
    },

    remainingTime: () => {
      const { expireAt } = getState();
      const result = expireAt - moment().unix();
      return result >= 0 ? result : -1;
    },

    remainingTimeString: () => {
      const time = getState().remainingTime();
      const timeM = Math.floor(time / 60);
      const timeS = time % 60;
      return `${timeM}:${timeS}`;
    },

    findItemById: (id: number) => {
      return getState().programs.find(p => p.id === id);
    },

    getTotalItemsCount: () => {
      let result = 0;
      for (const program of getState().programs) {
        result += program.selectedCount;
      }
      return result;
    },

    getMealCartCount: (programId: number) => {
      const line = getState().programs.find(p => p.id === programId);
      return line?.selectedCount ?? 0;
    },

    emptyBasket: () => {
      const payload = {
        programs: [],
        selectedSelfId: null,
        checkoutSelfDefId: null,
        weekStartDateTime: null,
      };
      setState(payload);
      writeCartToStorage({ ...getState(), ...payload });
    },
  };
};

export default create(combine(initialState, mutations as any));
